import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { uniqueBy } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';
import type { WithRewardRange } from '@sorare/core/src/lib/rewards';

import { RankRewardTitle } from '#src/components/leaderboard/CumulativeRankRewards/RankRewardTitle/index.tsx';
import { CumulativeRanking } from '#src/components/leaderboard/CumulativeRankRewards/Ranking/index.tsx';
import type { RewardRangeVariant } from '#src/components/leaderboard/RewardRange/index.tsx';
import { RewardRange } from '#src/components/leaderboard/RewardRange/index.tsx';
import { ClickableAnyRewardConfig } from '#src/components/rewards/ClickableAnyRewardConfig/index.tsx';

import type {
  CumulativeRankReward_So5UserGroupMembership,
  CumulativeRankReward_so5RewardConfig,
} from './__generated__/index.graphql.ts';

type Props = {
  so5RewardConfig: WithRewardRange<CumulativeRankReward_so5RewardConfig>;
  so5Ranking?: CumulativeRankReward_So5UserGroupMembership | null;
  overview?: boolean;
  isMyRange: boolean;
  userGroupSlug: string;
  isMyNextRange: boolean;
  rewardRangeVariant: RewardRangeVariant;
  activeLineupId?: string;
  withCollapsibleRanking?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string
  ) => string;
  scoreDistanceFromMyRanking?: number;
  doNotDisplayUsers?: boolean;
};
export const CumulativeRankReward = ({
  so5RewardConfig,
  so5Ranking,
  overview,
  isMyRange,
  isMyNextRange,
  rewardRangeVariant,
  userGroupSlug,
  activeLineupId,
  withCollapsibleRanking = false,
  rankingLink,
  scoreDistanceFromMyRanking,
  doNotDisplayUsers,
}: Props) => {
  const isMyRangeOrMyNextRange = isMyRange || isMyNextRange;
  const shouldDisplay = !overview || isMyRangeOrMyNextRange;

  if (!shouldDisplay) {
    return null;
  }

  return (
    <RewardRange
      key={so5RewardConfig.startRank}
      variant={rewardRangeVariant}
      title={
        <RankRewardTitle
          {...so5RewardConfig}
          scoreDistanceFromMyRanking={scoreDistanceFromMyRanking}
        />
      }
      rewards={so5RewardConfig.rewardConfigs.map((rewardConfig, i) => {
        return (
          <ClickableAnyRewardConfig
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            anyRewardConfig={rewardConfig}
          />
        );
      })}
    >
      {uniqueBy(
        ranking =>
          (
            (isType(ranking, 'So5UserGroupMembership') && ranking.ranking) ||
            0
          ).toString(),
        [
          so5RewardConfig.fromUserRanking,
          isMyRange && so5Ranking,
          so5RewardConfig.toUserRanking,
        ].filter(Boolean)
      ).map(
        ranking =>
          isType(ranking, 'So5UserGroupMembership') &&
          !doNotDisplayUsers && (
            <CumulativeRanking
              key={ranking.ranking}
              so5Ranking={ranking}
              userGroupSlug={userGroupSlug}
              activeLineupId={activeLineupId}
              isMyRanking={ranking.ranking === so5Ranking?.ranking}
              withCollapsibleRanking={withCollapsibleRanking}
              rankingLink={rankingLink}
            />
          )
      )}
    </RewardRange>
  );
};

CumulativeRankReward.fragments = {
  so5RewardConfig: gql`
    fragment CumulativeRankReward_so5RewardConfig on So5RewardConfig {
      id
      ranks
      rankPct
      fromUserRanking {
        id
        ... on So5UserGroupMembership {
          id
          ranking
          ...CumulativeRanking_So5UserGroupMembership
        }
      }
      toUserRanking {
        id
        ... on So5UserGroupMembership {
          id
          ranking
          ...CumulativeRanking_So5UserGroupMembership
        }
      }
      rewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${CumulativeRanking.fragments.So5UserGroupMembership}
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<CumulativeRankReward_so5RewardConfig>,
  So5UserGroupMembership: gql`
    fragment CumulativeRankReward_So5UserGroupMembership on So5UserGroupMembership {
      id
      ranking
      ...CumulativeRanking_So5UserGroupMembership
    }
    ${CumulativeRanking.fragments.So5UserGroupMembership}
  ` as TypedDocumentNode<CumulativeRankReward_So5UserGroupMembership>,
};
